import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ShieldAlert, KeyRound } from "lucide-react";

/**
 * Reusable approval dialog for Tier A (passphrase) or Tier B (passphrase + TOTP).
 * Props:
 *   open, onOpenChange, tier ("A" | "B"), title, description
 *   onConfirm(passphrase, totpCode) — async; throws on error.
 */
export default function TierApprovalDialog({ open, onOpenChange, tier, title, description, onConfirm }) {
  const [passphrase, setPassphrase] = React.useState("");
  const [totp, setTotp] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) { setPassphrase(""); setTotp(""); setError(null); }
  }, [open]);

  const accent = tier === "B" ? "var(--elle-oxblood)" : "var(--elle-amber)";
  const accentBright = tier === "B" ? "var(--elle-oxblood-bright)" : "var(--elle-amber-bright)";

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(passphrase, tier === "B" ? totp : null);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Verification failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="elle-surface border" style={{ borderColor: accent, color: "var(--elle-ink)" }}>
        <DialogHeader>
          <div className="flex items-center gap-2 overline" style={{ color: accentBright }}>
            {tier === "B" ? <ShieldAlert size={14} /> : <KeyRound size={14} />}
            Tier {tier} approval
          </div>
          <DialogTitle className="font-display text-2xl mt-1">{title}</DialogTitle>
          <DialogDescription style={{ color: "var(--elle-ink-dim)" }}>
            {description}
            {tier === "B" && (
              <span className="block mt-2 text-xs font-mono-elle" style={{ color: accentBright }}>
                Hard-limit change — passphrase AND 2FA code required.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="overline block mb-2" style={{ color: accentBright }}>Tier A passphrase</label>
            <Input data-testid={`tier-${tier.toLowerCase()}-passphrase`}
                   type="password" value={passphrase}
                   onChange={(e)=>setPassphrase(e.target.value)}
                   className="font-mono-elle" autoFocus />
          </div>
          {tier === "B" && (
            <div>
              <label className="overline block mb-2" style={{ color: accentBright }}>6-digit 2FA code</label>
              <Input data-testid="tier-b-totp"
                     inputMode="numeric" maxLength={6}
                     value={totp} onChange={(e)=>setTotp(e.target.value.replace(/\D/g,""))}
                     className="font-mono-elle tracking-widest text-lg"
                     placeholder="· · · · · ·" />
            </div>
          )}
          {error && <div className="text-sm" style={{ color: "var(--elle-oxblood-bright)" }}>{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="btn-ghost rounded-sm" onClick={()=>onOpenChange(false)}>
            Cancel
          </Button>
          <Button data-testid={`tier-${tier.toLowerCase()}-confirm-btn`}
                  onClick={submit} disabled={submitting || !passphrase || (tier==="B" && totp.length !== 6)}
                  className={tier === "B" ? "btn-oxblood rounded-sm" : "btn-amber rounded-sm"}>
            {submitting ? "Verifying…" : `Confirm (Tier ${tier})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
